// Group booking management (D-376, docs/group-sales.md). Pure: the pipeline's
// allowed moves, and the arithmetic a quote and the order it becomes must
// agree on.
import type { GroupLeadStatus } from '@bre-ticketing/contracts';

export interface GroupLeadTransition {
  readonly from: GroupLeadStatus;
  readonly to: GroupLeadStatus;
}

const move = (from: GroupLeadStatus, to: GroupLeadStatus): GroupLeadTransition => ({ from, to });

/**
 * The moves a lead may make. Mirrors `group_lead_status_transitions`
 * (migration 0121); the parity test holds the two together.
 *
 * Forward through the pipeline, with two ways back that happen in practice: a
 * hold that lapses returns the lead to QUOTED, and a quote that is not landing
 * goes back to CONTACTED. A lost lead that calls back is worked again. A
 * booked lead is finished by the game (COMPLETED) or by canceling.
 */
export const GROUP_LEAD_TRANSITIONS: readonly GroupLeadTransition[] = Object.freeze([
  move('NEW', 'CONTACTED'),
  move('NEW', 'QUOTED'),
  move('NEW', 'HOLD'),
  move('NEW', 'BOOKED'),
  move('NEW', 'LOST'),
  move('NEW', 'CANCELLED'),
  move('CONTACTED', 'QUOTED'),
  move('CONTACTED', 'HOLD'),
  move('CONTACTED', 'BOOKED'),
  move('CONTACTED', 'LOST'),
  move('CONTACTED', 'CANCELLED'),
  move('QUOTED', 'CONTACTED'),
  move('QUOTED', 'HOLD'),
  move('QUOTED', 'BOOKED'),
  move('QUOTED', 'LOST'),
  move('QUOTED', 'CANCELLED'),
  move('HOLD', 'QUOTED'),
  move('HOLD', 'BOOKED'),
  move('HOLD', 'LOST'),
  move('HOLD', 'CANCELLED'),
  move('BOOKED', 'COMPLETED'),
  move('BOOKED', 'CANCELLED'),
  move('LOST', 'CONTACTED'),
]);

/** The statuses that need a reason (why it was lost, why it was canceled). */
export const GROUP_LEAD_REASON_STATUSES: readonly GroupLeadStatus[] = Object.freeze(['LOST', 'CANCELLED']);

/** The statuses still somebody's to work. */
export const GROUP_LEAD_OPEN_STATUSES: readonly GroupLeadStatus[] = Object.freeze(['NEW', 'CONTACTED', 'QUOTED', 'HOLD', 'BOOKED']);

export function canMoveGroupLead(from: GroupLeadStatus, to: GroupLeadStatus): boolean {
  return GROUP_LEAD_TRANSITIONS.some((t) => t.from === from && t.to === to);
}

/**
 * How far along the pipeline a status is, for the moves the system makes on
 * its own: sending a quote moves a NEW or CONTACTED lead to QUOTED, but never
 * drags a lead already on hold back to QUOTED.
 */
const RANK: Record<GroupLeadStatus, number> = { NEW: 0, CONTACTED: 1, QUOTED: 2, HOLD: 3, BOOKED: 4, COMPLETED: 5, LOST: -1, CANCELLED: -1 };

/** The status an automatic step should leave the lead in, or null to leave it alone. */
export function advanceGroupLead(current: GroupLeadStatus, reached: GroupLeadStatus): GroupLeadStatus | null {
  if (current === reached) return null;
  if (RANK[current] < 0 && current !== 'LOST') return null;
  if (RANK[reached] <= RANK[current]) return null;
  return canMoveGroupLead(current, reached) ? reached : null;
}

/**
 * A discount, spread over what can carry it, in whole cents.
 *
 * A quote's discount comes off the space or package first (its line is the
 * group's to discount), then off the options, then off the tickets one by one
 * — never taking any of them below zero, and never splitting a cent that
 * would make a quote and its order disagree. Returns what each carrier gives
 * up, in the order given, or null when the discount is bigger than everything
 * together.
 */
export function allocateGroupDiscount(discountCents: number, carriers: readonly number[]): number[] | null {
  if (!Number.isInteger(discountCents) || discountCents < 0) return null;
  const total = carriers.reduce((sum, c) => sum + Math.max(0, c), 0);
  if (discountCents > total) return null;
  let left = discountCents;
  return carriers.map((carrier) => {
    const take = Math.min(Math.max(0, carrier), left);
    left -= take;
    return take;
  });
}

/**
 * A discount over N tickets of the same price, as per-ticket amounts that add
 * up exactly: the first `remainder` tickets carry one cent more.
 */
export function spreadOverTickets(discountCents: number, quantity: number, maxPerTicket: number): number[] | null {
  if (quantity <= 0) return discountCents === 0 ? [] : null;
  if (discountCents > maxPerTicket * quantity) return null;
  const base = Math.floor(discountCents / quantity);
  const remainder = discountCents - base * quantity;
  return Array.from({ length: quantity }, (_, i) => base + (i < remainder ? 1 : 0));
}

/** A lead's reference as the requester reads it: GRP- and five characters with nothing ambiguous. */
const REFERENCE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export function groupLeadReference(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): string {
  const bytes = random(5);
  let out = '';
  for (const byte of bytes) out += REFERENCE_ALPHABET[byte % REFERENCE_ALPHABET.length];
  return `GRP-${out}`;
}
